import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useJsApiLoader } from '@react-google-maps/api'

interface GoogleMapsContextValue {
  isLoaded: boolean
  loadError?: Error
  apiKey?: string
}

const GoogleMapsContext = createContext<GoogleMapsContextValue | undefined>(undefined)

const libraries: ('places' | 'marker')[] = ['places', 'marker']

interface GoogleMapsProviderProps {
  children: ReactNode
}

export function GoogleMapsProvider({ children }: GoogleMapsProviderProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined

  const shouldLoad = Boolean(apiKey)

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: apiKey || '',
    libraries,
    preventGoogleFontsLoading: true,
    version: 'weekly', // Use the latest weekly version to get PlaceAutocompleteElement support
  })

  const value = useMemo<GoogleMapsContextValue>(() => ({
    isLoaded: shouldLoad ? isLoaded : false,
    loadError: shouldLoad ? loadError : new Error('Missing Google Maps API key'),
    apiKey,
  }), [shouldLoad, isLoaded, loadError, apiKey])

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  )
}

export function useGoogleMaps() {
  const context = useContext(GoogleMapsContext)
  if (!context) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider')
  }
  return context
}
